import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useQuery, useSubscription } from "@apollo/client/react";

import { NEW_MESSAGE_SUBSCRIPTION, NOTIFICATIONS_QUERY } from "../constants/graphql";
import useAuth from "../hooks/useAuth";
import useNotification from "../hooks/notification";
import useGeneratedFilePolling from "../hooks/useGeneratedFilePolling";
import { useBanner } from "./banner";
import ExcelViewerModal from "../components/modals/ExcelViewerModal";
import {
  bannerTypes,
  ERROR_MESSAGE_CATEGORY,
  isBillingReport,
  notificationTitle,
} from "../utils/notificationMatching";

const NotificationCenterContext = createContext(null);

export function useNotificationCenter() {
  const context = useContext(NotificationCenterContext);
  if (!context) {
    throw new Error("useNotificationCenter must be used within a NotificationCenterProvider");
  }
  return context;
}

const openWindow = link => {
  const openedWindow = window.open(link, "_blank");
  if (!openedWindow) {
    alert("Unable to open. Please allow popups for this website.");
  }
};

// Owns background notification handling (subscription, generated-file polling,
// billing report viewer) so it runs regardless of which layout or bell is visible.
export function NotificationCenterProvider({ children }) {
  const [unread, setUnread] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { success, notifyError } = useNotification();
  const { banner, closeBanner } = useBanner();
  const skip = !isAuthenticated || !user?.id;
  const { data: { notifications = [] } = {}, refetch } = useQuery(NOTIFICATIONS_QUERY, {
    variables: { limit: 4 },
    skip,
  });

  const markRead = useCallback(() => setUnread(false), []);

  const markNewArrival = useCallback(() => {
    refetch();
    setUnread(true);
  }, [refetch]);

  const openBillingReport = useCallback(notification => {
    if (location.pathname.startsWith("/insights")) {
      const params = new URLSearchParams(location.search);
      if (notification.ownerId) {
        params.set("ownerId", notification.ownerId);
      }
      navigate({
        pathname: `/insights/reports/${notification.id}`,
        search: params.toString(),
      });
      return;
    }

    setSelectedNotification(notification);
  }, [location.pathname, location.search, navigate]);

  const handleCompletedFileNotification = useCallback((notification, bannerType) => {
    const config = bannerTypes[bannerType] || bannerTypes.report;

    closeBanner();
    if (isBillingReport(notification)) {
      openBillingReport(notification);
    } else {
      success({ hide: config.hide, message: config.message });
      openWindow(notification.link);
    }

    markNewArrival();
  }, [closeBanner, markNewArrival, openBillingReport, success]);

  useGeneratedFilePolling({ skip, onCompleted: handleCompletedFileNotification });

  useSubscription(NEW_MESSAGE_SUBSCRIPTION, {
    variables: { userId: user?.id },
    onData: async ({ data: subscriptionData }) => {
      const newMessage = subscriptionData.data.newMessage;
      const bannerConfig = banner.open ? bannerTypes[banner.type] : null;

      if (newMessage.category === ERROR_MESSAGE_CATEGORY) {
        if (bannerConfig) closeBanner();
        notifyError(notificationTitle(newMessage) || "Something went wrong");
        markNewArrival();
        return;
      }

      const isBilling = isBillingReport(newMessage);

      if (banner.open) {
        if (!bannerConfig) return;
        closeBanner();

        if (isBilling) {
          // For billing reports, wait for refetch to get the complete notification with proper title
          const { data } = await refetch();
          const notifications = data?.notifications || [];

          // Find the newest notification (first in array since they're ordered by createdAt desc)
          const newestNotification = notifications.find(isBillingReport);

          if (newestNotification) {
            openBillingReport({
              id: newestNotification.id,
              link: newestNotification.link,
              title: newestNotification.title,
              ownerId: newestNotification.ownerId,
            });
          }
        } else {
          success({ hide: bannerConfig.hide, message: bannerConfig.message });
          // For all other reports (PDFs, non-billing Excel), open in new tab
          if (newMessage.link) openWindow(newMessage.link);
        }
      } else if (!isBilling) {
        success({ hide: 10000, message: notificationTitle(newMessage) || "New message received" });
      }

      markNewArrival();
    },
    skip,
  });

  const value = useMemo(() => ({
    notifications,
    unread,
    markRead,
    refetch,
    openBillingReport,
  }), [notifications, unread, markRead, refetch, openBillingReport]);

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
      {selectedNotification && (
        <ExcelViewerModal
          open
          onClose={() => setSelectedNotification(null)}
          fileUrl={selectedNotification.link}
          fileName={selectedNotification.title}
        />
      )}
    </NotificationCenterContext.Provider>
  );
}
